import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '@store/hooks';

import { DEFAULT_POLL_INTERVAL_MS } from '../constants';
import { useGetNotificationConfigQuery } from '../store/api';
import { selectPersistentDismissedIds, selectSessionDismissedIds } from '../store/selectors';
import { type SystemNotification } from '../types';
import { doesNotificationMatchRoute } from '../utils/routeMatching';

export function useActiveNotifications() {
  const { pathname } = useLocation();

  // Use RTK Query with default polling interval
  // Note: RTK Query's pollingInterval is set at hook mount time and doesn't change dynamically
  const { data: config } = useGetNotificationConfigQuery(undefined, {
    pollingInterval: DEFAULT_POLL_INTERVAL_MS,
  });

  const persistentDismissed = useAppSelector(selectPersistentDismissedIds);
  const sessionDismissed = useAppSelector(selectSessionDismissedIds);

  const activeNotifications = useMemo((): SystemNotification[] => {
    if (!config?.notifications?.length) return [];

    const now = new Date();

    return config.notifications.filter(notification => {
      if (notification.startsAt && new Date(notification.startsAt) > now) return false;
      if (notification.expiresAt && new Date(notification.expiresAt) < now) return false;
      if (!doesNotificationMatchRoute(notification, pathname)) return false;

      const dismissedList = notification.persistent ? persistentDismissed : sessionDismissed;
      if (dismissedList.includes(notification.id)) return false;

      return true;
    });
  }, [config, pathname, persistentDismissed, sessionDismissed]);

  const banners = useMemo(
    () => activeNotifications.filter(n => n.type === 'banner'),
    [activeNotifications],
  );

  const modals = useMemo(
    () => activeNotifications.filter(n => n.type === 'modal'),
    [activeNotifications],
  );

  const highestSeverityBanner = useMemo(() => {
    const order: Array<'error' | 'warning' | 'info'> = ['error', 'warning', 'info'];
    return order.map(s => banners.find(b => b.severity === s)).find(Boolean) ?? null;
  }, [banners]);

  return { activeNotifications, banners, modals, highestSeverityBanner };
}
